package app.footyguide.web.page

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.web.components.*
import app.footyguide.core.theme.ClubCatalog
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.GameRepository
import app.footyguide.domain.model.MatchStatus
import app.footyguide.domain.model.isLive
import app.footyguide.core.locale.LocalStrings
import app.footyguide.web.data.WebGameRepository
import app.footyguide.web.route.WebRoutes

private fun normalizeChannelSlug(raw: String): String {
    val clean = raw.trim().lowercase()
    return when {
        clean.contains("globo") && !clean.contains("globoplay") -> "globo"
        clean.contains("sportv") -> "sportv"
        clean.contains("premiere") -> "premiere"
        clean.contains("caze") || clean.contains("cazé") -> "cazetv"
        clean.contains("record") -> "record"
        clean.contains("espn") -> "espn"
        clean.contains("prime") -> "prime"
        clean.contains("tnt") || clean.contains("max") -> "tnt"
        else -> clean
    }
}

fun formatChannelHeaderName(channelId: String): String {
    val clean = channelId.trim().lowercase()
    return when {
        clean == "globo" -> "Globo"
        clean == "sportv" -> "SporTV"
        clean == "premiere" -> "Premiere"
        clean == "cazetv" -> "CazéTV"
        clean == "record" -> "Record"
        clean == "prime" || clean == "primevideo" -> "Prime Video"
        clean == "espn" -> "ESPN"
        clean == "disney" -> "Disney+"
        clean == "tnt" || clean == "max" -> "TNT / Max"
        else -> channelId.replaceFirstChar { it.uppercase() }
    }
}

/**
 * Stitch Design System (Screen 6: Guia de Canais - O que Passa na TV Hoje).
 * Broad-Sheet Paper & Ink: #FAFAF7 substrate, #FFFFFF cards, #E7E7E1 borders,
 * clear broadcaster filter pills and graphical channel broadcast cards.
 */
@Composable
fun ChannelsPage(
    initialChannelId: String? = null,
    gameRepository: GameRepository = remember { WebGameRepository() },
    onNavigate: (String) -> Unit = {}
) {
    val strings = LocalStrings.current
    var selectedChannel by remember { mutableStateOf(initialChannelId ?: "todos") }
    val today = remember { app.footyguide.web.todayFormatted() }
    var todayGames by remember(today) { mutableStateOf<List<Game>>(emptyList()) }
    var isLoading by remember(today) { mutableStateOf(true) }

    LaunchedEffect(today) {
        isLoading = true
        todayGames = try {
            gameRepository.getGamesByDate(today)
        } catch (_: Exception) {
            emptyList()
        }
        isLoading = false
    }

    val channelsGuide = remember(todayGames) {
        val grouped = mutableMapOf<String, MutableList<Game>>()
        todayGames.forEach { game ->
            val channels = game.channelsFor("BR")
            channels.forEach { ch ->
                val slug = normalizeChannelSlug(ch)
                grouped.getOrPut(slug) { mutableListOf() }.add(game)
            }
        }
        grouped
    }

    val channelFilterList = listOf(
        Pair("todos", strings.allChannelsFilter),
        Pair("globo", "Globo"),
        Pair("sportv", "SporTV"),
        Pair("premiere", "Premiere"),
        Pair("cazetv", "CazéTV"),
        Pair("record", "Record"),
        Pair("espn", "ESPN"),
        Pair("prime", "Prime Video"),
        Pair("tnt", "TNT / Max")
    )

    // Collect games based on filter
    val displayGames: List<Pair<String, Game>> = remember(selectedChannel, channelsGuide) {
        if (selectedChannel == "todos") {
            channelsGuide.flatMap { (channelId, games) ->
                games.map { game -> Pair(channelId, game) }
            }.distinctBy { it.second.id }
        } else {
            channelsGuide[selectedChannel]?.map { game -> Pair(selectedChannel, game) } ?: emptyList()
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            backgroundColor(Color("#FAFAF7"))
            paddingBottom(80.px)
        }
    }) {
        AppToolbar(
            icon = { LucideIcon(Icons.TV, size = 20, color = "var(--accent-fill, #0B6E4F)") },
            title = strings.channelsGuide,
            actions = {
                Span({
                    style {
                        fontSize(11.px)
                        fontFamily("Inter, sans-serif")
                        fontWeight(700)
                        color(Color("var(--accent-fill, #0B6E4F)"))
                        backgroundColor(Color("rgba(var(--accent-fill-rgb, 11, 110, 79), 0.12)"))
                        border(1.px, LineStyle.Solid, Color("rgba(var(--accent-fill-rgb, 11, 110, 79), 0.25)"))
                        padding(4.px, 8.px)
                        borderRadius(999.px)
                        property("text-transform", "uppercase")
                    }
                }) {
                    Text(strings.todayOnTv)
                }
            }
        )

        Div({
            style {
                width(100.percent)
                maxWidth(600.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                backgroundColor(Color("#FAFAF7"))
            }
        }) {
            // CHANNELS FILTER BAR
            Div({
                style {
                    margin(14.px, 16.px, 8.px)
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    overflowX("auto")
                    property("scrollbar-width", "none")
                }
            }) {
                channelFilterList.forEach { (id, label) ->
                    val isSelected = selectedChannel == id
                    Button({
                        onClick { selectedChannel = id }
                        style {
                            minHeight(44.px)
                            padding(10.px, 16.px)
                            property("display", "inline-flex")
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            backgroundColor(Color(if (isSelected) "#16181D" else "#FFFFFF"))
                            color(Color(if (isSelected) "#FFFFFF" else "#71717A"))
                            border(1.px, LineStyle.Solid, Color(if (isSelected) "#16181D" else "#E7E7E1"))
                            borderRadius(999.px)
                            fontFamily("Inter, sans-serif")
                            fontSize(12.px)
                            fontWeight(if (isSelected) 700 else 600)
                            cursor("pointer")
                            whiteSpace("nowrap")
                        }
                    }) {
                        Text(label)
                    }
                }
            }

            // CHANNEL GAMES LIST
            Div({
                style {
                    padding(8.px, 16.px, 16.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(12.px)
                }
            }) {
                if (isLoading) {
                    ChannelsPageSkeleton()
                } else if (displayGames.isEmpty()) {
                    Div({
                        style {
                            padding(40.px, 20.px)
                            backgroundColor(Color("#FFFFFF"))
                            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                            borderRadius(16.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            property("text-align", "center")
                        }
                    }) {
                        LucideIcon(Icons.TV, size = 28, color = "#71717A")
                        P({
                            style {
                                margin(0.px)
                                fontSize(13.px)
                                fontFamily("Inter, sans-serif")
                                color(Color("#71717A"))
                            }
                        }) {
                            Text(strings.noGamesForChannel)
                        }
                    }
                } else {
                    displayGames.forEach { (channelId, game) ->
                        val homeClub = ClubCatalog.bySlug(game.homeTeamId)
                        val awayClub = ClubCatalog.bySlug(game.awayTeamId)
                        val homeName = homeClub?.name ?: game.homeTeamId.replaceFirstChar { it.uppercase() }
                        val awayName = awayClub?.name ?: game.awayTeamId.replaceFirstChar { it.uppercase() }
                        val homeInitials = homeClub?.initials ?: game.homeTeamId.take(3).uppercase()
                        val awayInitials = awayClub?.initials ?: game.awayTeamId.take(3).uppercase()
                        val isLive = game.isLive
                        val kickoffTime = extractKickoffTime(game.kickoff)
                        val channelBadge = formatChannelName(channelId)

                        val gameRoute = WebRoutes.game(game)

                        Div({
                            onClick { onNavigate(gameRoute) }
                            style {
                                backgroundColor(Color("#FFFFFF"))
                                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                                borderRadius(16.px)
                                padding(16.px)
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(10.px)
                                cursor("pointer")
                                property("box-shadow", "0 2px 6px rgba(22, 24, 29, 0.03)")
                            }
                        }) {
                            // TOP ROW: BROADCASTER BADGE + KICKOFF TIME
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    justifyContent(JustifyContent.SpaceBetween)
                                    alignItems(AlignItems.Center)
                                }
                            }) {
                                Span({
                                    style {
                                        fontSize(10.px)
                                        fontWeight(800)
                                        fontFamily("Inter, sans-serif")
                                        color(Color("#16181D"))
                                        backgroundColor(Color("#F2F2EC"))
                                        border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                                        padding(3.px, 8.px)
                                        borderRadius(6.px)
                                        property("text-transform", "uppercase")
                                        letterSpacing(0.4.px)
                                    }
                                }) {
                                    Text("📺 $channelBadge")
                                }

                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        gap(6.px)
                                    }
                                }) {
                                    if (isLive) {
                                        Span({
                                            style {
                                                fontSize(11.px)
                                                fontWeight(800)
                                                fontFamily("Inter, sans-serif")
                                                color(Color("#D92D20"))
                                                backgroundColor(Color("#FEF3F2"))
                                                padding(2.px, 6.px)
                                                borderRadius(4.px)
                                            }
                                        }) {
                                            Text(strings.liveLabel)
                                        }
                                    }
                                    Span({
                                        style {
                                            fontSize(12.px)
                                            fontWeight(700)
                                            fontFamily("Sora, sans-serif")
                                            color(Color("#16181D"))
                                        }
                                    }) {
                                        Text(kickoffTime)
                                    }
                                }
                            }

                            // MATCHUP ROW
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.SpaceBetween)
                                }
                            }) {
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        gap(10.px)
                                    }
                                }) {
                                    SafeImg(
                                        src = homeClub?.logoUrl,
                                        fallbackIcon = Icons.SHIELD,
                                        fallbackSize = CrestSizes.MATCH_CARD_FALLBACK,
                                        modifier = {
                                            width(CrestSizes.MATCH_CARD_PX.px)
                                            height(CrestSizes.MATCH_CARD_PX.px)
                                            property("width", CrestSizes.MATCH_CARD_CSS)
                                            property("height", CrestSizes.MATCH_CARD_CSS)
                                            property("object-fit", "contain")
                                        }
                                    )
                                    Span({
                                        style {
                                            fontFamily("Sora, sans-serif")
                                            fontSize(15.px)
                                            fontWeight(800)
                                            color(Color("#16181D"))
                                        }
                                    }) {
                                        Text("$homeInitials × $awayInitials")
                                    }
                                    SafeImg(
                                        src = awayClub?.logoUrl,
                                        fallbackIcon = Icons.SHIELD,
                                        fallbackSize = CrestSizes.MATCH_CARD_FALLBACK,
                                        modifier = {
                                            width(CrestSizes.MATCH_CARD_PX.px)
                                            height(CrestSizes.MATCH_CARD_PX.px)
                                            property("width", CrestSizes.MATCH_CARD_CSS)
                                            property("height", CrestSizes.MATCH_CARD_CSS)
                                            property("object-fit", "contain")
                                        }
                                    )
                                }

                                Div({
                                    style {
                                        fontSize(12.px)
                                        fontFamily("Inter, sans-serif")
                                        fontWeight(500)
                                        color(Color("#71717A"))
                                    }
                                }) {
                                    Text("$homeName x $awayName")
                                }
                            }

                            // FOOTER INFO
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    justifyContent(JustifyContent.SpaceBetween)
                                    alignItems(AlignItems.Center)
                                    paddingTop(6.px)
                                    property("border-top", "1px solid #F4F4F5")
                                }
                            }) {
                                Span({
                                    style {
                                        fontSize(11.px)
                                        fontFamily("Inter, sans-serif")
                                        color(Color("#71717A"))
                                    }
                                }) {
                                    Text(game.stadium ?: strings.regularPhase)
                                }

                                ChannelBadgesRow(game.channelsFor("BR"))
                            }
                        }
                    }
                }
            }
        }

        // BOTTOM NAV
        BottomNavBar(
            currentRoute = WebRoutes.CHANNELS,
            onNavigate = onNavigate
        )
    }
}

@Composable
private fun ChannelsPageSkeleton() {
    repeat(3) {
        Div({
            style {
                backgroundColor(Color("#FFFFFF"))
                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                borderRadius(16.px)
                padding(16.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(80.px); height(20.px); borderRadius(6.px) }
                })
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(44.px); height(16.px); borderRadius(4.px) }
                })
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(150.px); height(28.px); borderRadius(8.px) }
                })
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(110.px); height(16.px); borderRadius(4.px) }
                })
            }
        }
    }
}
